import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { getThemeColors } from '../../utils/theme';
import API_ENDPOINTS from '../../config';
import { authenticatedFetch } from '../../utils/api';
import logger from '../../utils/logger';

export default function AdminUploads() {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const [uploads, setUploads] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    companyId: '',
    userId: ''
  });

  const fetchUploads = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page,
        limit: 20,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.companyId && { companyId: filters.companyId }),
        ...(filters.userId && { userId: filters.userId })
      });

      const response = await authenticatedFetch(`${API_ENDPOINTS.API_BASE_URL}/api/admin/uploads?${params}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch uploads');
      }

      const data = await response.json();
      setUploads(data.uploads || []);
      setPagination(data.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
      });
    } catch (error) {
      // Don't log redirect errors - user is being redirected to login
      if (!error.redirected && error.message !== 'Unauthorized - redirected to login') {
        logger.error('Error fetching uploads:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters]);

  useEffect(() => {
    fetchUploads();
  }, [fetchUploads]);

  const handleDeleteUpload = async (uploadId) => {
    if (!window.confirm(t('admin.uploads.deleteConfirm'))) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.API_BASE_URL}/api/admin/uploads/${uploadId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete upload');
      }

      // Refresh uploads list
      fetchUploads(pagination?.page || 1);
    } catch (error) {
      // Don't log redirect errors - user is being redirected to login
      if (!error.redirected && error.message !== 'Unauthorized - redirected to login') {
        logger.error('Error deleting upload:', error);
        alert(t('admin.uploads.failedToLoad'));
      }
    }
  };

  const formatDate = (dateString) => {
    const localeMap = {
      'sr': 'sr-RS',
      'zh': 'zh-CN',
      'en': 'en-US'
    };
    const locale = localeMap[i18n.language] || 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading && uploads.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
        <div style={{ fontSize: '18px', color: colors.textSecondary }}>{t('admin.uploads.loading')}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: colors.text, margin: '0 0 0.5rem 0' }}>
          {t('admin.uploads.title')}
        </h1>
        <p style={{ color: colors.textSecondary, margin: 0 }}>
          {t('admin.uploads.subtitle')}
        </p>
      </div>

      {/* Search and Filters */}
      <div style={{
        background: colors.card,
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: colors.shadow,
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
              {t('admin.uploads.searchLabel')}
            </label>
            <input
              type="text"
              placeholder={t('admin.uploads.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                background: colors.background,
                color: colors.text
              }}
            />
          </div>
        </div>
      </div>

      {/* Uploads Table */}
      <div style={{
        background: colors.card,
        borderRadius: '12px',
        boxShadow: colors.shadow,
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: colors.backgroundLight }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.uploads.file')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.uploads.user')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.uploads.company')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.uploads.size')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.uploads.uploadDate')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'center', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.uploads.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {uploads.map((upload, index) => (
                <tr key={upload.id} style={{ 
                  borderBottom: index < uploads.length - 1 ? `1px solid ${colors.border}` : 'none',
                  background: index % 2 === 0 ? colors.card : colors.backgroundLight
                }}>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ fontSize: '24px' }}>📄</div>
                      <div>
                        <div style={{ fontWeight: 500, color: colors.text }}>
                          {upload.originalName}
                        </div>
                        <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                          {upload.filename}
                        </div>
                        <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                          {upload.mimetype}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontSize: '14px', color: colors.text }}>
                      {upload.User?.firstName} {upload.User?.lastName}
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                      {upload.User?.email}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontSize: '14px', color: colors.text }}>
                      {upload.Company?.name}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontSize: '14px', color: colors.text }}>
                      {formatBytes(upload.size)}
                    </div>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '14px', color: colors.textSecondary }}>
                    {formatDate(upload.createdAt)}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <button
                      onClick={() => handleDeleteUpload(upload.id)}
                      style={{
                        padding: '6px 12px',
                        background: colors.error,
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = isDark ? '#ef4444' : '#b91c1c'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = colors.error}
                    >
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination?.pages > 1 && (
          <div style={{ 
            padding: '1rem', 
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ fontSize: '14px', color: colors.textSecondary }}>
              {t('admin.uploads.showingFiles', {
                from: ((pagination.page - 1) * pagination.limit) + 1,
                to: Math.min(pagination.page * pagination.limit, pagination.total),
                total: pagination.total
              })}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => fetchUploads(pagination.page - 1)}
                disabled={pagination.page <= 1}
                style={{
                  padding: '8px 12px',
                  background: pagination.page <= 1 ? colors.backgroundLight : colors.primary,
                  color: pagination.page <= 1 ? colors.textLight : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer'
                }}
              >
                {t('common.previous')}
              </button>
              <span style={{ padding: '8px 12px', fontSize: '14px', color: colors.text }}>
                {t('admin.uploads.pageOf', { page: pagination.page, pages: pagination.pages })}
              </span>
              <button
                onClick={() => fetchUploads(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                style={{
                  padding: '8px 12px',
                  background: pagination.page >= pagination.pages ? colors.backgroundLight : colors.primary,
                  color: pagination.page >= pagination.pages ? colors.textLight : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: pagination.page >= pagination.pages ? 'not-allowed' : 'pointer'
                }}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 